import useBalanceLockContext from "@casino/legacy/client/components/Balance/context/BalanceLock/useBalanceLockContext";
import useCurrentUser from "@casino/legacy/client/hooks/user/useCurrentUser";
import type { BalanceFragment } from "@workspace/casino_core/queries/UserBalanceQuery";
import { useUserBalanceQuery } from "@workspace/casino_core/queries/UserBalanceQuery.client";
import { useMyBalanceChangedSubscription } from "@workspace/casino_core/subscriptions/MyBalanceChangedSubscription.client";
import { useMyDepositAddressUpdatedSubscription } from "@workspace/casino_core/subscriptions/MyDepositAddressUpdatedSubscription.client";
import { useEffect, useState } from "react";
import useDelayedBalance from "../useDelayedBalance";
import useDisplayAmount from "./useDisplayAmount";

const useUserBalance = (wallet?: CashierCurrency) => {
  const { user, walletCurrency } = useCurrentUser();
  const { locked, balance: lockedBalance } = useBalanceLockContext();
  const currency = wallet ?? walletCurrency;
  const { loading, data, refetch } = useUserBalanceQuery({
    skip: !currency,
    variables: { currency },
  });
  const [balance, setBalance] = useState<BalanceFragment>();
  useEffect(() => {
    setBalance(data?.banking?.myBalances?.edges?.[0]?.node);
  }, [data]);
  const delayedBalance = useDelayedBalance({ balance });

  useMyBalanceChangedSubscription({
    onData: ({ bankingMyBalanceChanged }) => {
      const changed = bankingMyBalanceChanged?.changedBalance;
      if (changed?.currency === currency) {
        setBalance(
          (previous) => ({ ...previous, ...changed }) as BalanceFragment,
        );
      }
    },
  });

  useMyDepositAddressUpdatedSubscription({
    variables: { walletCurrency },
    onData: () => refetch(),
  });

  const { realBalance, totalBonusBalance, cashierBalance, lockedRealBalance } =
    balance || {};

  const getBalance = () => {
    const balanceType = getBalanceType();
    if (locked && balanceType !== undefined && lockedBalance !== undefined) {
      return {
        ...balanceType,
        totalBalance: lockedBalance,
      };
    }
    return balanceType;
  };

  const getBalanceType = () => {
    if (balance?.currency !== delayedBalance?.currency) {
      return balance;
    }
    return delayedBalance || balance;
  };

  const { money, icon } = useDisplayAmount({ balance: getBalance() });
  return {
    loading,
    user,
    money,
    icon,
    realBalance,
    totalBalance: cashierBalance,
    totalBonusBalance,
    lockedRealBalance,
  };
};

export default useUserBalance;
